import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useOne,
  useCustom,
  useUpdate,
  useCustomMutation,
  useGo,
} from '@refinedev/core';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, AlertTriangle, FileText } from 'lucide-react';
import { notify } from '@/lib/notify';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { FormHeader, FormStickyActions, FormSpacer } from '@/components/forms';
import { LanguageTabs } from '@/components/universal';
import {
  EmailEditorPanel,
  type EmailDoc,
  type EmailEditorValue,
} from '@/components/email-editor';
import {
  MESSAGE_TEMPLATE_CONFIG,
  MESSAGE_TEMPLATE_LANGS,
  MessageTemplateType,
  isMessageTemplateType,
  isMessageTemplateLang,
  type MessageTemplate,
  type MessageTemplateLang,
  type MessageTemplateType as MessageTemplateTypeValue,
  type UpdateSingletonDto,
  type CreateNamedDto,
  type UpdateNamedDto,
  type PreviewDto,
  type PreviewResult,
} from './types';

interface PlainObject {
  [key: string]: unknown;
}

function isPlainObject(value: unknown): value is PlainObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isPreviewResult(value: unknown): value is PreviewResult {
  if (!isPlainObject(value)) {
    return false;
  }
  return typeof value.subject === 'string' && typeof value.html === 'string';
}

function isMessageTemplateArray(value: unknown): value is MessageTemplate[] {
  return Array.isArray(value);
}

const EMPTY_DOC: EmailDoc = { type: 'doc', content: [] };

// ─── Singleton-режим (письма / системные сообщения) ──────────────────────────

interface LangFormState {
  id: number;
  subject: string;
  docJson: EmailDoc;
  htmlTemplate: string;
  isActive: boolean;
  dirty: boolean;
}

interface PreviewState {
  open: boolean;
  loading: boolean;
  error: string | null;
  result: PreviewResult | null;
}

interface SingletonEditorProps {
  type: MessageTemplateTypeValue;
}

function SingletonEditor({ type }: SingletonEditorProps) {
  const config = MESSAGE_TEMPLATE_CONFIG[type];

  const byTypeResult = useCustom<MessageTemplate[]>({
    url: `/admin/message-templates/by-type/${type}`,
    method: 'get',
  });

  const isLoading = byTypeResult.query?.isLoading ?? false;

  const langRows = useMemo<MessageTemplate[]>(() => {
    const payload = byTypeResult.result?.data;
    return isMessageTemplateArray(payload) ? payload : [];
  }, [byTypeResult.result]);

  const availableLangs = useMemo(
    () =>
      MESSAGE_TEMPLATE_LANGS.filter((langMeta) =>
        langRows.some((row) => row.lang === langMeta.code),
      ),
    [langRows],
  );

  const [forms, setForms] = useState<Map<MessageTemplateLang, LangFormState>>(
    new Map(),
  );
  const [activeLang, setActiveLang] = useState<MessageTemplateLang>('ru');
  const [preview, setPreview] = useState<PreviewState>({
    open: false,
    loading: false,
    error: null,
    result: null,
  });

  useEffect(() => {
    if (langRows.length === 0) {
      return;
    }
    const next = new Map<MessageTemplateLang, LangFormState>();
    for (const row of langRows) {
      if (!isMessageTemplateLang(row.lang)) {
        continue;
      }
      next.set(row.lang, {
        id: row.id,
        subject: row.subject ?? '',
        docJson: row.docJson ?? EMPTY_DOC,
        htmlTemplate: row.htmlTemplate,
        isActive: row.isActive,
        dirty: false,
      });
    }
    setForms(next);
    const first = availableLangs[0]?.code;
    if (first) {
      setActiveLang(first);
    }
  }, [langRows, availableLangs]);

  const updateMutation = useUpdate();
  const isSaving = updateMutation.mutation?.isPending ?? false;
  const { mutate: fetchPreview } = useCustomMutation();

  const showSubject = config.requiresSubject;

  const setActiveFormField = useCallback(
    (patch: Partial<Omit<LangFormState, 'id'>>) => {
      setForms((prev) => {
        const current = prev.get(activeLang);
        if (!current) {
          return prev;
        }
        const next = new Map(prev);
        next.set(activeLang, { ...current, ...patch, dirty: true });
        return next;
      });
    },
    [activeLang],
  );

  const handleEditorChange = useCallback(
    (value: EmailEditorValue) => {
      setForms((prev) => {
        const current = prev.get(activeLang);
        if (!current) {
          return prev;
        }
        if (
          current.htmlTemplate === value.htmlTemplate &&
          JSON.stringify(current.docJson) === JSON.stringify(value.docJson)
        ) {
          return prev;
        }
        const next = new Map(prev);
        next.set(activeLang, {
          ...current,
          docJson: value.docJson,
          htmlTemplate: value.htmlTemplate,
          dirty: true,
        });
        return next;
      });
    },
    [activeLang],
  );

  const handleSaveLang = useCallback(
    (lang: MessageTemplateLang) => {
      const formState = forms.get(lang);
      if (!formState) {
        return;
      }
      const values: UpdateSingletonDto = {
        subject: formState.subject,
        docJson: formState.docJson,
        htmlTemplate: formState.htmlTemplate,
        isActive: formState.isActive,
      };
      updateMutation.mutate(
        { resource: 'message-templates', id: formState.id, values },
        {
          onSuccess: () => {
            setForms((prev) => {
              const current = prev.get(lang);
              if (!current) {
                return prev;
              }
              const next = new Map(prev);
              next.set(lang, { ...current, dirty: false });
              return next;
            });
          },
          onError: (error) => {
            notify.error(error.message || 'Не удалось сохранить шаблон');
          },
        },
      );
    },
    [forms, updateMutation],
  );

  const handleSaveAll = useCallback(() => {
    let dirtyCount = 0;
    for (const lang of forms.keys()) {
      const state = forms.get(lang);
      if (state?.dirty) {
        dirtyCount += 1;
        handleSaveLang(lang);
      }
    }
    if (dirtyCount === 0) {
      notify.info('Нет несохранённых изменений');
    }
  }, [forms, handleSaveLang]);

  const handlePreview = useCallback(() => {
    setPreview({ open: true, loading: true, error: null, result: null });
    const body: PreviewDto = {
      type,
      lang: activeLang,
      vars: config.previewVars,
    };
    fetchPreview(
      {
        url: '/admin/message-templates/preview',
        method: 'post',
        values: body,
      },
      {
        onSuccess: (response) => {
          const payload = response?.data;
          if (isPreviewResult(payload)) {
            setPreview({
              open: true,
              loading: false,
              error: null,
              result: payload,
            });
          } else {
            setPreview({
              open: true,
              loading: false,
              error: 'Бэкенд вернул неожиданный формат предпросмотра',
              result: null,
            });
          }
        },
        onError: (error) => {
          setPreview({
            open: true,
            loading: false,
            error: error.message || 'Не удалось получить предпросмотр',
            result: null,
          });
        },
      },
    );
  }, [type, activeLang, config.previewVars, fetchPreview]);

  const hasDirty = useMemo(
    () => [...forms.values()].some((state) => state.dirty),
    [forms],
  );

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const activeState = forms.get(activeLang);

  return (
    <div className="space-y-6">
      <FormHeader
        title={`Шаблон: ${config.label}`}
        description={config.description}
        sticky
      />

      <LanguageTabs
        languages={availableLangs.map((langMeta) => ({
          code: langMeta.code,
          label: langMeta.label,
        }))}
        activeLanguage={activeLang}
        onLanguageChange={(code) => {
          if (isMessageTemplateLang(code)) {
            setActiveLang(code);
          }
        }}
      />

      {activeState ? (
        <div className="space-y-6">
          {showSubject && (
            <div className="space-y-2">
              <Label htmlFor={`subject-${activeLang}`}>Тема</Label>
              <Input
                id={`subject-${activeLang}`}
                value={activeState.subject}
                onChange={(e) => setActiveFormField({ subject: e.target.value })}
                placeholder="Тема сообщения..."
                disabled={isSaving}
              />
            </div>
          )}

          <EmailEditorPanel
            key={`${type}-${activeLang}`}
            value={activeState.docJson}
            onChange={handleEditorChange}
            placeholders={[...config.placeholders]}
            readOnly={isSaving}
          />

          <div className="flex items-center gap-3">
            <Switch
              id={`active-${activeLang}`}
              checked={activeState.isActive}
              onCheckedChange={(checked) =>
                setActiveFormField({ isActive: checked })
              }
              disabled={isSaving}
            />
            <Label htmlFor={`active-${activeLang}`}>
              Шаблон активен (используется для отправки)
            </Label>
          </div>
        </div>
      ) : (
        <p className="text-muted-foreground">
          Нет данных для выбранного языка
        </p>
      )}

      <FormSpacer />

      <FormStickyActions
        primaryLabel="Сохранить"
        pendingLabel="Сохранение..."
        onPrimary={handleSaveAll}
        isPending={isSaving}
        disabled={!hasDirty}
        secondaryLabel="Предпросмотр"
        secondaryVariant="outline"
        onSecondary={handlePreview}
      />

      <Dialog
        open={preview.open}
        onOpenChange={(open) => setPreview((prev) => ({ ...prev, open }))}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Предпросмотр: {config.label} ({activeLang.toUpperCase()})
            </DialogTitle>
            <DialogDescription>
              {preview.result
                ? `Тема: ${preview.result.subject}`
                : 'Рендер с демо-значениями плейсхолдеров'}
            </DialogDescription>
          </DialogHeader>
          {preview.loading && (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          )}
          {preview.error && (
            <div className="flex flex-col items-center gap-3 py-8">
              <AlertTriangle className="h-10 w-10 text-destructive" />
              <p className="text-sm text-destructive">{preview.error}</p>
            </div>
          )}
          {preview.result && !preview.loading && (
            <div className="overflow-auto rounded-md bg-neutral-100 p-4 dark:bg-neutral-900">
              <iframe
                title="Предпросмотр сообщения"
                sandbox=""
                srcDoc={preview.result.html}
                className="mx-auto block w-full min-h-[600px] max-w-[640px] rounded-md border bg-white shadow-sm"
                style={{ minHeight: '600px' }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Multi-named-режим (Telegram-приглашения) ────────────────────────────────

interface NamedFormData {
  name: string;
  slug: string;
  htmlTemplate: string;
  isDefault: boolean;
  isActive: boolean;
}

const DEFAULT_TG_TEMPLATE = `Привет{{#if firstName}}, {{firstName}}{{/if}}!

Вас пригласили присоединиться к платформе {{platformName}}.

Для начала работы перейдите по ссылке:
{{botLink}}

До встречи на платформе!`;

function buildNamedState(template?: MessageTemplate): NamedFormData {
  if (!template) {
    return {
      name: '',
      slug: '',
      htmlTemplate: DEFAULT_TG_TEMPLATE,
      isDefault: false,
      isActive: true,
    };
  }
  return {
    name: template.name ?? '',
    slug: template.slug ?? '',
    htmlTemplate: template.htmlTemplate,
    isDefault: template.isDefault,
    isActive: template.isActive,
  };
}

interface NamedEditorProps {
  type: MessageTemplateTypeValue;
  isEdit: boolean;
  id?: number;
  initialState: NamedFormData;
}

function NamedEditor({ type, isEdit, id, initialState }: NamedEditorProps) {
  const go = useGo();
  const config = MESSAGE_TEMPLATE_CONFIG[type];
  const [formData, setFormData] = useState<NamedFormData>(initialState);

  const createMutation = useCustomMutation();
  const updateMutation = useUpdate();

  const isSubmitting =
    createMutation.mutation.isPending ||
    (updateMutation.mutation?.isPending ?? false);

  const handleChange = (
    field: keyof NamedFormData,
    value: string | boolean,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNameChange = (name: string) => {
    setFormData((prev) => ({
      ...prev,
      name,
      slug:
        !isEdit && !prev.slug
          ? name
              .toLowerCase()
              .replaceAll(/[^a-z0-9а-яё\s-]/gi, '')
              .replaceAll(/\s+/g, '-')
              .replaceAll(/-+/g, '-')
          : prev.slug,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      notify.error('Введите название шаблона');
      return;
    }
    if (!formData.slug.trim()) {
      notify.error('Введите slug шаблона');
      return;
    }
    if (!formData.htmlTemplate.trim()) {
      notify.error('Введите текст сообщения');
      return;
    }

    if (isEdit && id !== undefined) {
      const values: UpdateNamedDto = {
        name: formData.name,
        slug: formData.slug,
        htmlTemplate: formData.htmlTemplate,
        isDefault: formData.isDefault,
        isActive: formData.isActive,
      };
      updateMutation.mutate(
        {
          resource: 'message-templates',
          id: `named/${id}`,
          values,
        },
        {
          onSuccess: () => {
            go({ to: '/message-templates' });
          },
          onError: (error) => {
            notify.error(error.message || 'Ошибка обновления');
          },
        },
      );
    } else {
      const values: CreateNamedDto = {
        type,
        name: formData.name,
        slug: formData.slug,
        htmlTemplate: formData.htmlTemplate,
        isDefault: formData.isDefault,
        isActive: formData.isActive,
      };
      createMutation.mutate(
        {
          url: '/admin/message-templates/named',
          method: 'post',
          values,
        },
        {
          onSuccess: () => {
            notify.success('Шаблон создан');
            go({ to: '/message-templates' });
          },
          onError: (error) => {
            notify.error(error.message || 'Ошибка создания');
          },
        },
      );
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <FormHeader
        title={isEdit ? 'Редактировать шаблон' : 'Создать шаблон'}
        description={config.description}
        sticky
        icon={<FileText className="h-5 w-5" />}
        onBack={() => go({ to: '/message-templates' })}
      />

      <form
        id="message-template-named-form"
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        <Card>
          <CardHeader>
            <CardTitle>Основная информация</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mt-name">Название *</Label>
              <Input
                id="mt-name"
                placeholder="Стандартное приглашение"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mt-slug">Slug *</Label>
              <Input
                id="mt-slug"
                placeholder="standard-invitation"
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                Уникальный идентификатор шаблона (латиница, цифры, дефисы)
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Активен</Label>
                <p className="text-sm text-muted-foreground">
                  Неактивные шаблоны недоступны для выбора
                </p>
              </div>
              <Switch
                checked={formData.isActive}
                onCheckedChange={(checked) =>
                  handleChange('isActive', checked)
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>По умолчанию</Label>
                <p className="text-sm text-muted-foreground">
                  Будет использоваться, если шаблон не выбран явно
                </p>
              </div>
              <Switch
                checked={formData.isDefault}
                onCheckedChange={(checked) =>
                  handleChange('isDefault', checked)
                }
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Текст сообщения</CardTitle>
            <CardDescription>
              Используйте переменные Handlebars для персонализации
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-3 text-sm">
              <p className="font-medium mb-2">Доступные переменные:</p>
              <div className="flex flex-wrap gap-2">
                {config.placeholders.map((ph) => (
                  <code
                    key={ph}
                    className="bg-background px-2 py-1 rounded text-xs"
                  >
                    {ph}
                  </code>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mt-body">Текст сообщения *</Label>
              <Textarea
                id="mt-body"
                placeholder="Введите текст сообщения..."
                value={formData.htmlTemplate}
                onChange={(e) =>
                  handleChange('htmlTemplate', e.target.value)
                }
                rows={12}
                className="font-mono text-sm"
              />
            </div>
          </CardContent>
        </Card>
      </form>

      <FormSpacer />

      <FormStickyActions>
        <Button
          type="submit"
          form="message-template-named-form"
          disabled={isSubmitting}
        >
          {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {isEdit ? 'Сохранить' : 'Создать'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => go({ to: '/message-templates' })}
        >
          Отмена
        </Button>
      </FormStickyActions>
    </div>
  );
}

// ─── Роутер режима по multiplicity ───────────────────────────────────────────

/**
 * Создание именованного шаблона. Тип берётся из query (?type=...),
 * по умолчанию INVITATION_TELEGRAM (единственный multi-named тип).
 */
export function MessageTemplatesCreate() {
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const type: MessageTemplateTypeValue =
    typeParam && isMessageTemplateType(typeParam)
      ? typeParam
      : MessageTemplateType.INVITATION_TELEGRAM;

  if (MESSAGE_TEMPLATE_CONFIG[type].multiplicity !== 'multi-named') {
    return (
      <div className="space-y-6">
        <FormHeader title="Создание недоступно" sticky />
        <p className="text-muted-foreground">
          Шаблоны этого типа создаются автоматически и доступны только для
          редактирования.
        </p>
      </div>
    );
  }

  return (
    <NamedEditor
      type={type}
      isEdit={false}
      initialState={buildNamedState()}
    />
  );
}

/**
 * Редактирование шаблона по id. Грузим строку, определяем тип и
 * множественность, ветвимся: singleton → EmailEditorPanel + языки,
 * multi-named → форма имени/slug/текста.
 */
export function MessageTemplatesEdit() {
  const { id } = useParams<{ id: string }>();

  const oneResult = useOne<MessageTemplate>({
    resource: 'message-templates',
    id: Number(id),
  });
  const base = oneResult.result;
  const isLoading = oneResult.query?.isLoading ?? false;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!base || !isMessageTemplateType(base.type)) {
    return (
      <div className="space-y-6">
        <FormHeader title="Шаблон не найден" sticky />
        <div className="flex flex-col items-center gap-4 py-12">
          <AlertTriangle className="h-12 w-12 text-muted-foreground" />
          <p className="text-muted-foreground">Шаблон с ID {id} не найден</p>
        </div>
      </div>
    );
  }

  const config = MESSAGE_TEMPLATE_CONFIG[base.type];

  if (config.multiplicity === 'multi-named') {
    return (
      <NamedEditor
        key={`named-${base.id}`}
        type={base.type}
        isEdit
        id={base.id}
        initialState={buildNamedState(base)}
      />
    );
  }

  return <SingletonEditor key={`singleton-${base.type}`} type={base.type} />;
}
